import React, { useMemo, useEffect, useState } from 'react';
import { Typography } from 'antd';
import { ResponsiveLine } from '@nivo/line';
import '../../App.css';

interface TraitProps {
  mobile?: boolean;
  result?: number;
  traitsDescription?: { low: string; medium: string; high: string };
  gameCode?: 'BAR2' | 'TOW2';
  terciles?: [number, number];
  traitCode?: string;
}

const Trait = ({ mobile = false, result = 0, traitsDescription = {low:'', medium:'', high:''}, gameCode = 'BAR2', terciles = [40, 70], traitCode = ''}: TraitProps) => {
   // @ts-ignore
   const [t1, t2] = terciles;

   // Distribución por trait
  const distributionAVRRSG =  [
    {"name": "0-10", "val": 9},
    {"name": "10-20", "val": 72},
    {"name": "20-30", "val": 233},
    {"name": "30-40", "val": 327},
    {"name": "40-50", "val": 571},
    {"name": "50-60", "val": 1497},
    {"name": "60-70", "val": 1592},
    {"name": "70-80", "val": 486},
    {"name": "80-90", "val": 28},
    {"name": "90-100", "val": 2}
    ]

  const distributionCAPPLN = [
    {"name": "0-10", "val": 1},
    {"name": "10-20", "val": 3},
    {"name": "20-30", "val": 12},
    {"name": "30-40", "val": 21},
    {"name": "40-50", "val": 160},
    {"name": "50-60", "val": 480},
    {"name": "60-70", "val": 985},
    {"name": "70-80", "val": 585},
    {"name": "80-90", "val": 294},
    {"name": "90-100", "val": 52}
  ];

  const distributionEXTPLN = [
    {"name": "0-10", "val": 1},
    {"name": "10-20", "val": 2},
    {"name": "20-30", "val": 5},
    {"name": "30-40", "val": 12},
    {"name": "40-50", "val": 30},
    {"name": "50-60", "val": 175},
    {"name": "60-70", "val": 435},
    {"name": "70-80", "val": 923},
    {"name": "80-90", "val": 523},
    {"name": "90-100", "val": 178}
  ];

  const distributionsByTrait: Record<string, typeof distributionAVRRSG> = {
    AVRRSG: distributionAVRRSG,
    CAPPLN: distributionCAPPLN,
    EXTPLN: distributionEXTPLN,
  };

  const distribution = distributionsByTrait[traitCode]
    ?? (gameCode === 'TOW2' ? distributionCAPPLN : distributionAVRRSG);

  const [level, setLevel] = useState('low')

  useEffect(() => {
    if (result < t1) setLevel('low')
    else if (result < t2) setLevel('medium')
    else setLevel('high')
  }, [result, t1, t2])
   
  function calculatePercentage (value: number){
    return 25 + (value * 950) / 100;
  };

  const buildSegments = () => {
      // las bandas de color usan los mismos terciles que definen el nivel (low/medium/high)
      return (
        [
          {
            color: "#EFDBFF",
            percentage: [0, t1],
          },
          {
            color: "#D3ADF7",
            percentage: [t1, t2],
          },
          {
            color: "#B37FEB",
            percentage: [t2, 100],
          },
        ]
      );
    }

  const drawLines = () => {
    const dist = distribution;
    // @ts-ignore
    const total = dist.reduce((acc, obj) => acc + obj.val, 0);
    let newSegmentsPerso = [...Array(5)].map((i, idx) => dist[2 * idx].val + dist[2 * idx + 1].val);
    newSegmentsPerso = newSegmentsPerso.map(val => (val * 100 / total));
    const config = buildSegments();
    return config.map((line) => (
      <line
        x1={calculatePercentage(line.percentage[0])}
        x2={calculatePercentage(line.percentage[1])}
        y1="0"
        y2="0"
        stroke={line.color}
        strokeWidth="12"
      />
    ));
  };
  
  // @ts-ignore
  const [graphData, totalY, totalCandidates] = useMemo(
    // @ts-ignore
    (_) => {
      const tmpGraph = distribution
        ? distribution.map(({ name, val }) => ({
          x: name,
          y: val,
        }))
        : [];

      return [
        tmpGraph,
        tmpGraph.reduce((total, { y }) => total + y, 0),
        [
          {
            id: 'candidates',
            color: '#B37FEB',
            border: 'black',
            data: tmpGraph,
          },
        ],
      ];
    },
    [distribution]
  );
  return (
    <div>
          <div
            style={{
                height: 100,
                width: mobile ? '98%' : 'calc(100% - 55px)',
                marginLeft: mobile ? '0px' : '28px',
                marginBottom: mobile ? '-7px' : '-16px',
            }}
          >
             <ResponsiveLine
              data={totalCandidates}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                stacked: true,
                reverse: false,
              }}
              curve="basis"
              axisTop={null}
              axisRight={null}
              axisBottom={null}
              axisLeft={null}
              enableGridX={false}
              enableGridY={false}
              enableArea={true}
              colors={(value) => value.color}
              enablePoints={false}
              pointSize={10}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabel="y"
              pointLabelYOffset={-12}
              useMesh={true}
              legends={[]}
            />
          </div>
        
          <svg
          width="100%"
          viewBox={`0 -20 1000 90`}
          xmlns="http://www.w3.org/2000/svg"
        // there is 1000px of witdh available to paint (viexBox autoscale vectors)
        >
          {drawLines()}
          {new Array(11).fill(10).map((n, i) => (
            <React.Fragment key={i}>
              <text
                x={25 + (950 / 10) * i}
                y="40"
                fontSize="16"
                textAnchor="middle"
                fill="#5E5E5E"
              >
                {n * i}
              </text>
              <line
                x1={25 + (950 / 10) * i}
                x2={25 + (950 / 10) * i}
                y1="14"
                y2="20"
                stroke="#D9D9D9"
                strokeWidth="2"
                strokeLinecap="square"
                name="marker"
              />
            </React.Fragment>
          ))}
          <circle
            cx={25 + (950 * result) / 100}
            cy="0"
            r="15"
            fill="#000000"
          />
          <text
            x={25 + (950 * result) / 100}
            y="5"
            fontSize="14"
            fill="#FFFFFF"
            textAnchor="middle"
          >
            {result}
          </text>
        </svg>

        <Typography.Text style={{ fontSize: '2vh'}} >
        {// @ts-ignore
        traitsDescription[level]}
        </Typography.Text> 

    </div>
  );
};

export default Trait;